// src/pages/AdminDashboard.js
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Empty,
  Flex,
  Input,
  Popconfirm,
  Segmented,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  SearchOutlined,
  SendOutlined,
  WarningOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axiosInstance from "../api/axiosInstance";
import LoadingSpinner from "../components/LoadingSpinner";
import "./AdminDashboard.css";
import notify from "../utils/notify";

const { Title, Text } = Typography;

/* ================= helpers ================= */

const toDateOrNull = (v) => {
  if (!v) return null;
  if (v instanceof Date) return v;
  const s = String(v).trim().replace(" ", "T");
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const pick = (obj, ...keys) => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return undefined;
};

const normalizeUserId = (u) =>
  String(u?.userId ?? u?.id ?? u?.Id ?? u?.userID ?? "");

const fmtDateTime = (d) => {
  if (!d) return "—";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
};

const minsUntil = (d) => {
  if (!d) return null;
  const ms = d.getTime() - Date.now();
  return Math.round(ms / 60000);
};

// If API sends multiple expiries, take the earliest available one
const getExpiryDate = (user) => {
  const candidates = [
    pick(
      user,
      "tokenExpiresAt",
      "expiresAt",
      "expiryDate",
      "expiry",
      "kycTokenExpiresAt",
      "approvalTokenExpiresAt"
    ),
    pick(user, "approveTokenExpiresAt", "approveTokenExpiry"),
    pick(user, "rejectTokenExpiresAt", "rejectTokenExpiry"),
  ]
    .map(toDateOrNull)
    .filter(Boolean);

  if (!candidates.length) return null;
  candidates.sort((a, b) => a.getTime() - b.getTime());
  return candidates[0];
};

const expiryMeta = (d) => {
  if (!d) return { label: "—", kind: "neutral" };

  const m = minsUntil(d);
  if (m === null) return { label: fmtDateTime(d), kind: "neutral" };

  if (m < 0) return { label: `Expired · ${fmtDateTime(d)}`, kind: "expired" };
  if (m <= 60) return { label: `In ${m} min · ${fmtDateTime(d)}`, kind: "warn" };
  if (m <= 24 * 60)
    return { label: `In ${Math.round(m / 60)} hr · ${fmtDateTime(d)}`, kind: "soon" };

  return { label: fmtDateTime(d), kind: "neutral" };
};

const isExpiredTokenMessage = (msg) => {
  const s = String(msg || "").toLowerCase();
  return (
    s.includes("expired token") ||
    s.includes("token expired") ||
    s.includes("invalid or expired") ||
    (s.includes("invalid") && s.includes("token"))
  );
};

const initials = (firstName, lastName) => {
  const a = String(firstName || "").trim();
  const b = String(lastName || "").trim();
  const i1 = a ? a[0].toUpperCase() : "";
  const i2 = b ? b[0].toUpperCase() : "";
  return (i1 + i2) || "?";
};

/* ================= component ================= */

export default function AdminDashboard() {
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableBusy, setTableBusy] = useState(false);

  // server-confirmed expired tokens (even when API doesn't provide expiry date)
  const [tokenStateByUserId, setTokenStateByUserId] = useState({});

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | active | expired

  const { authData, handleLogout } = useAuth();
  const navigate = useNavigate();


  const token = authData?.token;

  const authHeader = useMemo(() => {
    if (!token) return {};
    return { Authorization: `Bearer ${token}` };
  }, [token]);

  const markRowExpired = useCallback((userId, reason = "Token expired") => {
    setTokenStateByUserId((prev) => ({
      ...prev,
      [userId]: {
        expired: true,
        reason,
        detectedAt: new Date().toISOString(),
      },
    }));
  }, []);

  const fetchPendingUsers = useCallback(async () => {
    if (!token) {
      setPendingUsers([]);
      setLoading(false);
      return;
    }

    try {
      setTableBusy(true);

      const res = await axiosInstance.get("/Admin/pending-kyc-users", {
        headers: authHeader,
      });

      const users = Array.isArray(res.data) ? res.data : [];
      setPendingUsers(users);
    } catch (error) {
      if (error.response?.status === 401) {
        notify.error({
          message: "Session expired",
          description: "Please log in again.",
        });
        handleLogout();
        navigate("/login", { replace: true });
        return;
      }

      notify.error({
        message: "Failed to load pending users",
        description: error.response?.data?.message || "Please try again.",
      });
    } finally {
      setTableBusy(false);
      setLoading(false);
    }
  }, [token, authHeader, handleLogout, navigate]);

  useEffect(() => {
    fetchPendingUsers();
  }, [fetchPendingUsers]);

  const handleApprove = useCallback(
    async (user) => {
      const userId = user?.userId;
      if (!user?.approveToken) {
        notify.warning("Approve token is missing for this user.");
        return;
      }

      try {
        setTableBusy(true);

        const res = await axiosInstance.post(
          "/Admin/approve-kyc",
          { token: user.approveToken },
          { headers: authHeader }
        );

        notify.success({
          message: "KYC Approved",
          description: res.data?.message || "KYC approved successfully.",
        });

        setPendingUsers((prev) => prev.filter((u) => normalizeUserId(u) !== String(userId)));
      } catch (err) {
        const apiMsg =
          err?.response?.data?.message || "An error occurred while approving KYC.";

        if (isExpiredTokenMessage(apiMsg)) {
          notify.error({
            message: "Token expired",
            description: "Request the user to re-submit KYC.",
          });
          markRowExpired(String(userId), apiMsg);
          return;
        }

        notify.error({ message: "Approve failed", description: apiMsg });
      } finally {
        setTableBusy(false);
      }
    },
    [authHeader, markRowExpired]
  );

  const handleReject = useCallback(
    async (user) => {
      const userId = user?.userId;
      if (!user?.rejectToken) {
        notify.warning("Reject token is missing for this user.");
        return;
      }

      try {
        setTableBusy(true);

        const res = await axiosInstance.post(
          "/Admin/reject-kyc",
          { token: user.rejectToken },
          { headers: authHeader }
        );

        notify.success({
          message: "KYC Rejected",
          description: res.data?.message || "KYC rejected successfully.",
        });

        setPendingUsers((prev) => prev.filter((u) => normalizeUserId(u) !== String(userId)));
      } catch (err) {
        const apiMsg =
          err?.response?.data?.message || "An error occurred while rejecting KYC.";

        if (isExpiredTokenMessage(apiMsg)) {
          notify.error({
            message: "Token expired",
            description: "Request the user to re-submit KYC.",
          });
          markRowExpired(String(userId), apiMsg);
          return;
        }

        notify.error({ message: "Reject failed", description: apiMsg });
      } finally {
        setTableBusy(false);
      }
    },
    [authHeader, markRowExpired]
  );

  const handleRequestResubmit = useCallback(
    async (user) => {
      const userId = user?.userId;
      if (!userId) return;

      try {
        setTableBusy(true);

        const res = await axiosInstance.post(
          "/Admin/kyc/request-resubmission",
          { userId },
          { headers: authHeader }
        );

        notify.success({
          message: "Resubmission requested",
          description: res.data?.message || "The user has been notified to re-submit KYC.",
        });

        markRowExpired(String(userId), "Resubmission requested.");
      } catch (err) {
        notify.error({
          message: "Request failed",
          description: err?.response?.data?.message || "Failed to notify user.",
        });
      } finally {
        setTableBusy(false);
      }
    },
    [authHeader, markRowExpired]
  );

  const rows = useMemo(() => {
    const s = search.trim().toLowerCase();

    const mapped = (pendingUsers || []).map((u) => {
      const userId = normalizeUserId(u);
      const exp = getExpiryDate(u);

      const serverExpired = tokenStateByUserId?.[userId]?.expired === true;
      const timeExpired = exp ? exp.getTime() < Date.now() : false;
      const isExpired = Boolean(timeExpired || serverExpired);

      let expView = expiryMeta(exp);
      if (!exp && serverExpired) {
        const reason = tokenStateByUserId?.[userId]?.reason || "Expired";
        expView = { label: `Expired · ${reason}`, kind: "expired" };
      }

      return {
        ...u,
        key: userId,
        userId,
        _expiryDate: exp,
        _expiryLabel: expView.label,
        _expiryKind: expView.kind, // neutral | soon | warn | expired
        _isExpired: isExpired,
      };
    });

    const filteredBySearch = mapped.filter((r) => {
      if (!s) return true;
      const name = `${r.firstName || ""} ${r.lastName || ""}`.trim().toLowerCase();
      const email = String(r.email || "").toLowerCase();
      return name.includes(s) || email.includes(s);
    });

    const filteredByStatus =
      statusFilter === "all"
        ? filteredBySearch
        : statusFilter === "expired"
        ? filteredBySearch.filter((r) => r._isExpired)
        : filteredBySearch.filter((r) => !r._isExpired);

    return filteredByStatus;
  }, [pendingUsers, search, statusFilter, tokenStateByUserId]);

  const counts = useMemo(() => {
    const all = (pendingUsers || []).length;
    const expired = (pendingUsers || []).filter((u) => {
      const userId = normalizeUserId(u);
      const exp = getExpiryDate(u);
      const serverExpired = tokenStateByUserId?.[userId]?.expired === true;
      const timeExpired = exp ? exp.getTime() < Date.now() : false;
      return Boolean(timeExpired || serverExpired);
    }).length;
    const active = all - expired;
    return { all, active, expired };
  }, [pendingUsers, tokenStateByUserId]);

  const columns = useMemo(
    () => [
      {
        title: "User",
        key: "user",
        ellipsis: true,
        render: (_, r) => (
          <Space size={12}>
            <Avatar icon={<UserOutlined />} className="kyc-avatar">
              {initials(r.firstName, r.lastName)}
            </Avatar>

            <div style={{ minWidth: 0 }}>
              <div className="kyc-name-line">
                <Text strong className="kyc-name">
                  {(r.firstName || "") + " " + (r.lastName || "")}
                </Text>

                {r._isExpired ? (
                  <Tag color="red" className="kyc-pill">
                    Expired
                  </Tag>
                ) : (
                  <Tag color="blue" className="kyc-pill">
                    Active
                  </Tag>
                )}
              </div>

              <Text type="secondary" className="kyc-email">
                {r.email}
              </Text>
            </div>
          </Space>
        ),
        sorter: (a, b) =>
          `${a.firstName || ""} ${a.lastName || ""}`.localeCompare(
            `${b.firstName || ""} ${b.lastName || ""}`
          ),
      },
      {
        title: "Expiry",
        key: "expiry",
        width: 260,
        render: (_, r) => {
          const tagProps =
            r._expiryKind === "expired"
              ? { color: "red", icon: <WarningOutlined /> }
              : r._expiryKind === "warn"
              ? { color: "orange", icon: <WarningOutlined /> }
              : r._expiryKind === "soon"
              ? { color: "blue" }
              : { color: "default" };

          return (
            <div className="kyc-expiry">
              <Tag {...tagProps} className="kyc-expiry-tag">
                {r._expiryLabel}
              </Tag>
              <Text type="secondary" className="kyc-expiry-sub">
                {r._isExpired ? "Approval links disabled" : "Approval links valid"}
              </Text>
            </div>
          );
        },
        sorter: (a, b) => {
          const ta = a._expiryDate ? a._expiryDate.getTime() : Number.POSITIVE_INFINITY;
          const tb = b._expiryDate ? b._expiryDate.getTime() : Number.POSITIVE_INFINITY;
          return ta - tb;
        },
      },
      {
        title: "Action",
        key: "action",
        width: 340,
        render: (_, r) => {
          if (r._isExpired) {
            return (
              <Space>
                <Popconfirm
                  title="Request re-submission?"
                  description={`Notify ${r.firstName} ${r.lastName} to re-submit KYC?`}
                  okText="Send"
                  cancelText="Cancel"
                  onConfirm={() => handleRequestResubmit(r)}
                >
                  <Button type="primary" icon={<SendOutlined />} loading={tableBusy}>
                    Request Re-Submit
                  </Button>
                </Popconfirm>

                <Tooltip title="Expired requests cannot be approved/rejected.">
                  <Button disabled>Approve</Button>
                </Tooltip>
              </Space>
            );
          }

          return (
            <Space>
              <Popconfirm
                title="Approve KYC?"
                description={`Approve KYC for ${r.firstName} ${r.lastName}?`}
                okText="Approve"
                cancelText="Cancel"
                okButtonProps={{ type: "primary" }}
                onConfirm={() => handleApprove(r)}
              >
                <Button type="primary" icon={<CheckCircleOutlined />} loading={tableBusy}>
                  Approve
                </Button>
              </Popconfirm>

              <Popconfirm
                title="Reject KYC?"
                description={`Reject KYC for ${r.firstName} ${r.lastName}?`}
                okText="Reject"
                cancelText="Cancel"
                okButtonProps={{ danger: true }}
                onConfirm={() => handleReject(r)}
              >
                <Button danger icon={<CloseCircleOutlined />} loading={tableBusy}>
                  Reject
                </Button>
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    [handleApprove, handleReject, handleRequestResubmit, tableBusy]
  );

  if (loading) return <LoadingSpinner />;

  return (
    <div className="admin-dashboard-page">

      <Card
        className="admin-dashboard-card"
        styles={{ body: { padding: 16 } }}
        title={
          <Flex align="center" justify="space-between" wrap="wrap" gap={12}>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                Pending KYC Users
              </Title>
              <Text type="secondary">
                Approve, reject, or request re-submission for expired links.
              </Text>
            </div>

            <Space wrap>
              <Input
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                prefix={<SearchOutlined />}
                placeholder="Search name or email..."
                style={{ width: 280 }}
              />

              <Button icon={<ReloadOutlined />} onClick={fetchPendingUsers} loading={tableBusy}>
                Refresh
              </Button>
            </Space>
          </Flex>
        }
        extra={
          <Segmented
            value={statusFilter}
            onChange={setStatusFilter}
            options={[
              { label: `All (${counts.all})`, value: "all" },
              { label: `Active (${counts.active})`, value: "active" },
              { label: `Expired (${counts.expired})`, value: "expired" },
            ]}
          />
        }
      >
        <div className="kyc-stats">
          <Badge count={counts.all} showZero />
          <Tag color="blue">Active: {counts.active}</Tag>
          <Tag color="red">Expired: {counts.expired}</Tag>
        </div>

        <Table
          columns={columns}
          dataSource={rows}
          loading={tableBusy}
          size="middle"
          className="kyc-table"
          rowClassName={(r) => (r._isExpired ? "kyc-row-expired" : "")}
          pagination={{ pageSize: 8, showSizeChanger: true }}
          locale={{
            emptyText: (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No pending KYC users found." />
            ),
          }}
        />
      </Card>
    </div>
  );
}
